import { useState, useCallback, useEffect, useMemo } from 'react';

/**
 * Hook for managing column visibility state and handlers
 * @param {Object} options - Configuration options
 * @param {Array} options.columns - Column configuration array
 * @param {string} options.tableTitle - Table title for localStorage key
 * @returns {Object} Column visibility state and handlers
 */
const useColumnVisibility = ({ columns = [], tableTitle }) => {
  const [columnsState, setColumnsState] = useState(columns);
  const [allColumns, setAllColumns] = useState(true);
  const [columnSelectionOpened, setColumnSelectionOpened] = useState(false);
  const [columnSelectionHovered, setColumnSelectionHovered] = useState(false);

  const checkForAllColumns = useCallback((cols) => {
    const allVisible = cols.every((col) => !col.hidden);
    setAllColumns(allVisible);
  }, []);

  // Load saved column states from localStorage
  useEffect(() => {
    const newColumnsState = columns.map((col) => {
      const saved = localStorage.getItem(`${tableTitle}_${col.id}`);
      return {
        ...col,
        hidden: (col.alwaysHidden || saved === 'false') && !col.alwaysShown,
      };
    });
    setColumnsState(newColumnsState);
    checkForAllColumns(newColumnsState);
  }, [columns, tableTitle, checkForAllColumns]);

  const handleSelectColumn = useCallback(
    (col) => {
      const newColumnsState = columnsState.map((c) => {
        if (c.id === col.id) {
          return { ...c, hidden: !c.hidden };
        }
        return c;
      });
      setColumnsState(newColumnsState);
      checkForAllColumns(newColumnsState);
      localStorage.setItem(`${tableTitle}_${col.id}`, !col.hidden);
    },
    [columnsState, checkForAllColumns, tableTitle]
  );

  const handleSelectAllColumns = useCallback(() => {
    const newColumnsState = columnsState.map((col) => ({
      ...col,
      hidden: !!col.alwaysHidden,
    }));
    setColumnsState(newColumnsState);
    setAllColumns(true);
    newColumnsState.forEach((col) => {
      localStorage.setItem(`${tableTitle}_${col.id}`, true);
    });
  }, [columnsState, tableTitle]);

  const handleLeavePencil = useCallback(() => {
    setTimeout(() => {
      if (!columnSelectionHovered) {
        setColumnSelectionOpened(false);
      }
    }, 500);
  }, [columnSelectionHovered]);

  const visibleColumns = useMemo(() => columnsState.filter((col) => !col.hidden), [columnsState]);

  return {
    columnsState,
    visibleColumns,
    allColumns,
    columnSelectionOpened,
    columnSelectionHovered,
    setColumnSelectionOpened,
    setColumnSelectionHovered,
    handleSelectColumn,
    handleSelectAllColumns,
    handleLeavePencil,
  };
};

export default useColumnVisibility;
